import ProvenanceTag from "@/components/ProvenanceTag";
import { RadialScore } from "@/components/ScoreMeter";
import { TrendingUp, Swords, Users, Package, DollarSign, Rocket } from "lucide-react";

function Section({ icon: Icon, title, children }) {
  return (
    <div className="glass rounded-xl p-5">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-cyan-400" />
        <h4 className="font-semibold text-slate-100">{title}</h4>
      </div>
      {children}
    </div>
  );
}

function List({ items }) {
  return (
    <ul className="space-y-1.5">
      {(items || []).map((it, i) => (
        <li key={i} className="text-sm text-slate-300 flex gap-2"><span className="text-cyan-500 mt-1">▸</span>{it}</li>
      ))}
    </ul>
  );
}

function KV({ k, v }) {
  return <p className="text-sm text-slate-300 mb-1.5"><span className="text-slate-500">{k}: </span>{v}</p>;
}

export default function AnalysisReport({ report }) {
  if (!report) return null;
  const v = report.verdict || {};
  const m = report.market || {}, c = report.competition || {}, cu = report.customer || {},
    p = report.product || {}, mo = report.monetization || {}, g = report.gtm || {};
  return (
    <div className="space-y-6" data-testid="analysis-report">
      <div className="glass rounded-xl p-6 flex flex-col sm:flex-row items-center gap-6 glow-cyan">
        <RadialScore value={v.score || 0} size={110} label="Build Score" testId="verdict-score" />
        <div className="flex-1 text-center sm:text-left">
          <div className="flex items-center gap-2 justify-center sm:justify-start mb-2"><ProvenanceTag type="ai" /></div>
          <h3 className="font-display text-2xl font-bold text-slate-50">{v.label || "Analysis"}</h3>
          <p className="text-slate-300 mt-2">{v.explanation}</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Section icon={TrendingUp} title="Market">
          <KV k="Size" v={m.market_size} /><KV k="Direction" v={m.market_direction} />
          <KV k="Demand" v={m.customer_demand} /><KV k="Search interest" v={m.search_interest} />
          <p className="text-xs text-slate-500 mt-2 mb-1">Emerging trends</p><List items={m.emerging_trends} />
        </Section>
        <Section icon={Swords} title="Competition">
          <p className="text-xs text-slate-500 mb-1">Direct competitors</p><List items={c.direct_competitors} />
          <p className="text-xs text-slate-500 mt-2 mb-1">Their weaknesses</p><List items={c.competitor_weaknesses} />
          <p className="text-xs text-slate-500 mt-2 mb-1">Your differentiation</p><List items={c.differentiation} />
        </Section>
        <Section icon={Users} title="Customer">
          <KV k="Ideal customer" v={cu.ideal_customer} /><KV k="Willingness to pay" v={cu.willingness_to_pay} />
          <p className="text-xs text-slate-500 mt-2 mb-1">Pain points</p><List items={cu.pain_points} />
          <p className="text-xs text-slate-500 mt-2 mb-1">Buying triggers</p><List items={cu.buying_triggers} />
        </Section>
        <Section icon={Package} title="Product">
          <KV k="Recommended MVP" v={p.recommended_mvp} />
          <p className="text-xs text-slate-500 mt-2 mb-1">Core features</p><List items={p.core_features} />
          <p className="text-xs text-slate-500 mt-2 mb-1">AI features</p><List items={p.ai_features} />
        </Section>
        <Section icon={DollarSign} title="Monetization">
          <KV k="Recommended" v={mo.recommended} /><KV k="Pricing" v={mo.pricing} />
          <p className="text-sm text-slate-300 mt-1">{mo.reasoning}</p>
          <p className="text-xs text-slate-500 mt-2 mb-1">Models</p><List items={mo.models} />
        </Section>
        <Section icon={Rocket} title="Go-To-Market">
          <p className="text-xs text-slate-500 mb-1">Channels</p><List items={g.channels} />
          <p className="text-xs text-slate-500 mt-2 mb-1">First moves</p><List items={g.first_moves} />
          <p className="text-sm text-slate-300 mt-2">{g.content_strategy}</p>
        </Section>
      </div>
    </div>
  );
}
